(() => {
  if (!window.customElements || customElements.get('product-masonry')) return;

  const supportsNativeGridLanes =
    window.CSS && CSS.supports('display', 'grid-lanes');
  const supportsObservation = window.ResizeObserver && window.MutationObserver;
  const supportsFallback =
    window.CSS &&
    CSS.supports('display', 'grid') &&
    supportsObservation;

  class ProductMasonry extends HTMLElement {
    constructor() {
      super();
      this.grid = null;
      this.resizeObserver = null;
      this.mutationObserver = null;
      this.observedItems = new Set();
      this.frame = null;
      this.needsPreparation = false;
      this.gridWidth = null;
      this.layoutSignature = '';
      this.placedColumns = null;
      this.placedRowGap = null;
      this.nativeMode = false;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;

      this.grid = Array.from(this.children).find((child) =>
        child.hasAttribute('data-product-masonry-grid')
      );

      if (!this.grid) return;

      if (supportsNativeGridLanes) {
        this.initializeNativeGridLanes();
        return;
      }

      if (!supportsFallback) return;

      try {
        this.initialized = true;
        this.resizeObserver = new ResizeObserver((entries) => {
          let widthChanged = false;
          let itemChanged = false;

          entries.forEach((entry) => {
            if (entry.target === this.grid) {
              const nextWidth = entry.contentRect.width;

              if (this.gridWidth === null || Math.abs(nextWidth - this.gridWidth) > 0.5) {
                this.gridWidth = nextWidth;
                widthChanged = true;
              }
            } else {
              itemChanged = true;
            }
          });

          if (widthChanged || itemChanged) {
            this.scheduleLayout(widthChanged);
          }
        });
        this.mutationObserver = new MutationObserver(() => {
          this.syncItemObservers();
          this.scheduleLayout(true);
        });

        this.resizeObserver.observe(this.grid);
        this.syncItemObservers();
        this.mutationObserver.observe(this.grid, { childList: true });
        this.scheduleLayout(true);

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            if (this.isConnected) this.scheduleLayout(false);
          });
        }
      } catch (error) {
        this.restoreRowGrid();
      }
    }

    initializeNativeGridLanes() {
      try {
        this.initialized = true;
        this.nativeMode = true;

        if (supportsObservation) {
          this.resizeObserver = new ResizeObserver(() => {
            this.scheduleNativeAlignment();
          });
          this.mutationObserver = new MutationObserver(() => {
            this.scheduleNativeAlignment();
          });
          this.resizeObserver.observe(this.grid);
          this.mutationObserver.observe(this.grid, { childList: true });
        }

        this.scheduleNativeAlignment();
      } catch (error) {
        this.restoreRowGrid();
      }
    }

    disconnectedCallback() {
      this.restoreRowGrid();
    }

    syncItemObservers() {
      const items = new Set(this.grid ? Array.from(this.grid.children) : []);

      this.observedItems.forEach((item) => {
        if (!items.has(item)) {
          this.resizeObserver.unobserve(item);
          this.observedItems.delete(item);
        }
      });

      items.forEach((item) => {
        if (!this.observedItems.has(item)) {
          this.resizeObserver.observe(item);
          this.observedItems.add(item);
        }
      });
    }

    scheduleNativeAlignment() {
      if (!this.initialized || !this.nativeMode || !this.isConnected) return;
      if (this.frame !== null) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;

        if (!this.initialized || !this.nativeMode || !this.isConnected) return;
        this.alignTrailingItems();
      });
    }

    alignTrailingItems() {
      if (!this.grid || this.grid.getBoundingClientRect().width <= 0) return;

      const columns = Math.max(
        1,
        Number.parseInt(
          getComputedStyle(this).getPropertyValue('--product-masonry-columns'),
          10
        ) || 1
      );
      const items = Array.from(this.grid.children);
      const remainder = items.length % columns;
      const trailingStart = remainder === 0 ? items.length : items.length - remainder;

      items.forEach((item, index) => {
        item.style.removeProperty('grid-row');

        if (index >= trailingStart) {
          item.style.gridColumn = String(index - trailingStart + 1);
        } else {
          item.style.removeProperty('grid-column');
        }
      });

      this.placedColumns = columns;
    }

    scheduleLayout(needsPreparation) {
      if (!this.initialized || !this.isConnected) return;

      this.needsPreparation = this.needsPreparation || needsPreparation;
      if (this.frame !== null) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;

        if (!this.initialized || !this.isConnected) return;

        if (this.needsPreparation) {
          this.needsPreparation = false;
          this.prepareGrid();
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.measureAndPlace();
          });
          return;
        }

        this.measureAndPlace();
      });
    }

    prepareGrid() {
      this.layoutSignature = '';
      this.placedColumns = null;
      this.placedRowGap = null;
      this.clearItemPlacement();
      this.classList.remove('product-masonry--fallback');
      this.classList.add('product-masonry--measuring');
    }

    measureAndPlace() {
      if (!this.grid || !this.grid.isConnected) return;

      try {
        const items = Array.from(this.grid.children);
        const gridRect = this.grid.getBoundingClientRect();

        if (gridRect.width <= 0) return;

        const styles = getComputedStyle(this);
        const columns = Math.max(
          1,
          Number.parseInt(styles.getPropertyValue('--product-masonry-columns'), 10) || 1
        );
        const isFallback = this.classList.contains('product-masonry--fallback');
        const rowGap = isFallback
          ? this.placedRowGap || 0
          : Math.max(0, Math.round(Number.parseFloat(getComputedStyle(this.grid).rowGap) || 0));

        if (isFallback && columns !== this.placedColumns) {
          this.scheduleLayout(true);
          return;
        }

        const heights = items.map((item) =>
          Math.max(1, Math.ceil(item.getBoundingClientRect().height))
        );
        const signature = [
          Math.round(gridRect.width * 100) / 100,
          columns,
          rowGap,
          ...heights,
        ].join(':');

        if (
          signature === this.layoutSignature &&
          this.classList.contains('product-masonry--fallback')
        ) {
          return;
        }

        const laneEnds = Array(columns).fill(0);
        const remainder = heights.length % columns;
        const trailingStart = remainder === 0 ? heights.length : heights.length - remainder;
        const placements = heights.map((height, itemIndex) => {
          let lane = 0;

          if (itemIndex >= trailingStart) {
            lane = itemIndex - trailingStart;
          } else {
            for (let index = 1; index < columns; index += 1) {
              if (laneEnds[index] < laneEnds[lane]) lane = index;
            }
          }

          const start = laneEnds[lane] === 0 ? 1 : laneEnds[lane] + rowGap + 1;
          laneEnds[lane] = start + height - 1;

          return { lane: lane + 1, start, span: height };
        });

        items.forEach((item, index) => {
          const placement = placements[index];
          item.style.gridColumn = String(placement.lane);
          item.style.gridRow = `${placement.start} / span ${placement.span}`;
        });

        this.layoutSignature = signature;
        this.placedColumns = columns;
        this.placedRowGap = rowGap;
        this.classList.remove('product-masonry--measuring');
        this.classList.add('product-masonry--fallback');
      } catch (error) {
        this.restoreRowGrid();
      }
    }

    clearItemPlacement() {
      if (!this.grid) return;

      Array.from(this.grid.children).forEach((item) => {
        item.style.removeProperty('grid-column');
        item.style.removeProperty('grid-row');
      });
    }

    restoreRowGrid() {
      if (this.frame !== null) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();

      this.clearItemPlacement();
      this.classList.remove('product-masonry--measuring', 'product-masonry--fallback');
      this.observedItems.clear();
      this.resizeObserver = null;
      this.mutationObserver = null;
      this.gridWidth = null;
      this.layoutSignature = '';
      this.placedColumns = null;
      this.placedRowGap = null;
      this.needsPreparation = false;
      this.nativeMode = false;
      this.initialized = false;
    }
  }

  customElements.define('product-masonry', ProductMasonry);
})();
